import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
    Upload,
    CheckCircle2,
    AlertCircle,
    Loader2,
    RotateCcw,
    FileSpreadsheet,
    Info,
    Download,
    Users,
    IndianRupee,
    Navigation,
    HandCoins,
} from 'lucide-react';

import {
    uploadAdvanceStaging, fetchAdvanceStagingPreview,
    generateLabourAdvance, fetchAdvanceDetail,
    clearAdvanceState, clearGenerateResult,
} from '../../slices/labourAdvanceSlice/labourAdvanceSlice';
import { fetchAllCostCenters } from '../../slices/labourConfigSlice/labourConfigSlice';
import {
    parseAdvanceSheetRows, advanceRowError, buildGenerateAdvancePayload, isOwnLabour,
} from './labourAdvanceHelpers';

// Bulk Labour Advance generation (MOID 702) - look and flow copied from LabourPayrollGeneration.
// Available = daily rate (LabourMinWageConfig, CC + category) x days present - unsettled advance.

// ─── shared ui helpers ────────────────────────────────────────────────────────
const cn = (...c) => c.filter(Boolean).join(' ');

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 ' +
    'focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 ' +
    'hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const selectCls = inputCls;

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ children, title, icon: Icon, titleRight }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
        {title && (
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-3 bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
                {Icon && <Icon className="h-4 w-4 text-indigo-500" />}
                <span className="text-sm font-bold text-gray-800 dark:text-gray-100 flex-1">{title}</span>
                {titleRight}
            </div>
        )}
        {children}
    </div>
);

const Btn = ({ children, onClick, loading, disabled, variant = 'primary', size = 'md', className = '' }) => {
    const base = 'inline-flex items-center gap-1.5 font-semibold rounded-xl transition-all focus:outline-none shadow-sm';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-5 py-2.5 text-sm' };
    const variants = {
        primary:   'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white disabled:opacity-50 shadow-md',
        secondary: 'border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-700 bg-white dark:bg-gray-800',
        success:   'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white disabled:opacity-50 shadow-md',
    };
    return (
        <button
            onClick={onClick}
            disabled={loading || disabled}
            className={cn(base, sizes[size], variants[variant], className)}
        >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const MONTHS = [
    { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
    { v: 4, l: 'April' },   { v: 5, l: 'May' },       { v: 6, l: 'June' },
    { v: 7, l: 'July' },    { v: 8, l: 'August' },    { v: 9, l: 'September' },
    { v: 10, l: 'October' },{ v: 11, l: 'November' }, { v: 12, l: 'December' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const blankForm = () => ({
    ccCode: '', month: new Date().getMonth() + 1, year: CURRENT_YEAR, advanceDate: todayISO(),
});

const readSheet = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'array' });
                resolve(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' }));
            } catch (ex) {
                reject(ex.message || 'Failed to read Excel file');
            }
        };
        reader.onerror = () => reject('Failed to read file');
        reader.readAsArrayBuffer(file);
    });

// ─── Step indicator (white on gradient) ──────────────────────────────────────
const Steps = ({ current }) => {
    const steps = ['Setup & Upload', 'Preview & Amounts', 'Summary'];
    return (
        <div className="flex items-center">
            {steps.map((s, i) => (
                <React.Fragment key={i}>
                    <div className="flex flex-col items-center">
                        <div className={cn(
                            'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all',
                            current === i ? 'bg-white/20 border-white text-white' :
                            current > i  ? 'bg-white border-white text-indigo-600' :
                                           'bg-white/10 border-white/30 text-white/50'
                        )}>
                            {current > i ? <CheckCircle2 className="h-4 w-4" /> : <span>{i + 1}</span>}
                        </div>
                        <span className={cn('mt-1 text-xs font-medium hidden sm:block',
                            current === i ? 'text-white' :
                            current > i  ? 'text-indigo-200' : 'text-white/40'
                        )}>
                            {s}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={cn('flex-1 h-0.5 mx-2 mb-4 rounded', current > i ? 'bg-white/60' : 'bg-white/20')} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

// ─── Summary view (GetDetail after Generate) ─────────────────────────────────
const SummaryView = ({ detail, onReset }) => {
    const h = detail?.Header;
    const rows = detail?.Details || [];
    if (!h) return null;
    const monthName = MONTHS.find(m => m.v === h.AdvanceMonth)?.l || h.AdvanceMonth;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" /> Advance Generated & Submitted for Approval
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">{h.CCName} — {monthName} {h.AdvanceYear}</p>
                </div>
                <Btn variant="secondary" onClick={onReset}>
                    <RotateCcw className="h-3.5 w-3.5" /> New Advance
                </Btn>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SectionCard title="Advance Details" icon={IndianRupee}>
                    <div className="p-5 space-y-1 text-sm">
                        {[
                            ['Ref No', h.TransactionRefNo],
                            ['Cost Centre', `${h.CCCode} — ${h.CCName}`],
                            ['Period', `${monthName} ${h.AdvanceYear}`],
                            ['Advance Date', h.AdvanceDate],
                            ['Wages DCA', h.DCACode],
                            ['Budget FY', h.FYYear],
                            ['Status', h.Status],
                            ['Generated By', h.CreatedBy],
                            ['Generated On', h.CreatedOn],
                        ].map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-3 py-1 border-b border-gray-50 dark:border-gray-700">
                                <span className="text-gray-500 dark:text-gray-400">{k}</span>
                                <span className="text-gray-800 dark:text-gray-100 font-medium text-right">{v ?? '—'}</span>
                            </div>
                        ))}
                    </div>
                </SectionCard>

                <SectionCard title="Totals" icon={Users}>
                    <div className="p-5 text-sm">
                        {[
                            ['Workers', h.WorkerCount, false],
                            ['Own Labour Advance', `₹${fmt(h.OwnLabourAdvance)}`, false],
                            ['Contractor Labour Advance', `₹${fmt(h.ContractorAdvance)}`, false],
                            ['Total Advance', `₹${fmt(h.TotalAdvance)}`, true],
                        ].map(([k, v, bold]) => (
                            <div key={k} className={cn('flex justify-between py-1.5', bold && 'font-bold border-t border-gray-200 dark:border-gray-600 mt-1 pt-2')}>
                                <span className="text-gray-600 dark:text-gray-400">{k}</span>
                                <span className="text-gray-800 dark:text-gray-100">{v}</span>
                            </div>
                        ))}
                        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            The total is debited from the wages DCA budget now and given back if the advance is rejected.
                        </p>
                    </div>
                </SectionCard>
            </div>

            {rows.length > 0 && (
                <SectionCard title="Labour-wise Advance">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40">
                                    {['#', 'Labour ID', 'Name', 'Cat', 'Type', 'Days', 'Daily Rate', 'Earned', 'Unsettled', 'Available', 'Advance'].map(t => (
                                        <th key={t} className="px-3 py-2 text-left font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {rows.map((r, i) => (
                                    <tr key={r.DetailId || r.LabourId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10">
                                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                                        <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">{r.LabourId}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200 whitespace-nowrap">{r.LabourName}</td>
                                        <td className="px-3 py-2">{r.Category}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">{isOwnLabour(r) ? 'Own Labour' : `Contractor — ${r.ContractorName || r.ContractorCode || ''}`}</td>
                                        <td className="px-3 py-2 text-center">{r.DaysWorked}</td>
                                        <td className="px-3 py-2 text-right">₹{fmt(r.DailyRate)}</td>
                                        <td className="px-3 py-2 text-right">₹{fmt(r.EarnedAmount)}</td>
                                        <td className="px-3 py-2 text-right">₹{fmt(r.UnsettledBefore)}</td>
                                        <td className="px-3 py-2 text-right">₹{fmt(r.AvailableAmount)}</td>
                                        <td className="px-3 py-2 text-right font-bold text-emerald-600 dark:text-emerald-400">₹{fmt(r.AdvanceAmount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const LabourAdvanceGeneration = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const { costCenters } = useSelector(s => s.labourConfig);
    const {
        uploadBatchId, stagingLoading, stagingError,
        previewRows, previewLoading, previewError,
        generateLoading, generateResult, generateError,
        detail, detailLoading, detailError,
    } = useSelector(s => s.labourAdvance);

    const [step, setStep]             = useState(0);
    const [form, setForm]             = useState(blankForm);
    const [parsedRows, setParsedRows] = useState([]);
    const [excelFile, setExcelFile]   = useState(null);
    const [parseError, setParseError] = useState('');
    const [amounts, setAmounts]       = useState({});
    const [lastGenerateError, setLastGenerateError] = useState('');

    const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        dispatch(fetchAllCostCenters());
        return () => { dispatch(clearAdvanceState()); };
    }, [dispatch]);

    // A new sheet upload -> load the preview for that CC / period
    useEffect(() => {
        if (uploadBatchId && form.ccCode) {
            dispatch(fetchAdvanceStagingPreview({ ccCode: form.ccCode, month: form.month, year: form.year }));
            setAmounts({});
            setStep(1);
        }
    }, [uploadBatchId]); // eslint-disable-line

    useEffect(() => {
        if (generateResult) {
            if (generateResult.AdvanceId) {
                toast.success('Advance generated and submitted for approval');
                dispatch(fetchAdvanceDetail(generateResult.AdvanceId));
                setStep(2);
            } else {
                toast.error(generateResult.Message || 'Generation failed');
            }
            dispatch(clearGenerateResult());
        }
        if (generateError) {
            toast.error('Advance generation failed. See details below.');
            setLastGenerateError(generateError);
            dispatch(clearGenerateResult());
        }
    }, [generateResult, generateError]); // eslint-disable-line

    const handleDownloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([
            ['LabourId', 'Name', 'Category', 'Days Present'],
            ['LB001', 'Sample Worker 1', 'SK', 6],
            ['LB002', 'Sample Worker 2', 'USK', 5.5],
        ]);
        ws['!cols'] = [{ wch: 14 }, { wch: 24 }, { wch: 10 }, { wch: 14 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Advance');
        XLSX.writeFile(wb, 'LabourAdvance_Template.xlsx');
    };

    const parseFile = async (file, month, year) => {
        setParseError('');
        setParsedRows([]);
        try {
            const { rows, error } = parseAdvanceSheetRows(await readSheet(file), month, year);
            if (error) setParseError(error); else setParsedRows(rows);
        } catch (msg) {
            setParseError(String(msg));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setExcelFile(file);
        parseFile(file, form.month, form.year);
    };

    // The days check depends on the month length - re-check the chosen file when the period changes
    const setPeriod = (k, v) => {
        const next = { ...form, [k]: v };
        setForm(next);
        if (excelFile) parseFile(excelFile, next.month, next.year);
    };

    const handleUpload = () => {
        if (!form.ccCode)       { toast.warning('Select a Cost Centre'); return; }
        if (!parsedRows.length) { toast.warning('Upload a valid advance Excel sheet'); return; }
        dispatch(uploadAdvanceStaging({
            CCCode:       form.ccCode,
            AdvanceMonth: parseInt(form.month, 10),
            AdvanceYear:  parseInt(form.year, 10),
            UploadedBy:   userData?.userName || userData?.empCode || '',
            Rows:         parsedRows,
        }));
    };

    const handleReset = () => {
        dispatch(clearAdvanceState());
        setStep(0);
        setForm(blankForm());
        setParsedRows([]);
        setExcelFile(null);
        setParseError('');
        setAmounts({});
        setLastGenerateError('');
    };

    // ── Step 0: Setup + Upload
    const renderSetup = () => (
        <div className="space-y-6">
            <SectionCard title="Advance Period" icon={IndianRupee}>
                <div className="p-6 md:p-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="sm:col-span-2">
                            <Label required>Cost Centre</Label>
                            <select className={selectCls} value={form.ccCode} onChange={e => setField('ccCode', e.target.value)}>
                                <option value="">— Select CC —</option>
                                {costCenters.map(c => (
                                    <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label required>Month</Label>
                            <select className={selectCls} value={form.month} onChange={e => setPeriod('month', parseInt(e.target.value, 10))}>
                                {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label required>Year</Label>
                            <select className={selectCls} value={form.year} onChange={e => setPeriod('year', parseInt(e.target.value, 10))}>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label required>Advance Date</Label>
                            <input type="date" className={inputCls} value={form.advanceDate}
                                onChange={e => setField('advanceDate', e.target.value)} />
                        </div>
                    </div>
                </div>
            </SectionCard>

            <SectionCard title="Advance Excel Upload" icon={FileSpreadsheet}>
                <div className="p-6 md:p-8 space-y-4">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-start gap-2">
                            <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                Excel columns: <strong>LabourId, Name, Category (SK/SSK/USK/HSK), Days Present</strong>.
                                Days Present is counted from the 1st of the month up to today (week 1 = 6, week 2 = 12 ...),
                                so a second advance in the same month uses the full count again. Available advance =
                                daily rate × days present − advance not yet recovered in a payroll.
                            </p>
                        </div>
                        <button
                            onClick={handleDownloadTemplate}
                            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-semibold transition-all shadow-sm"
                        >
                            <Download className="h-3.5 w-3.5" /> Download Template
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        <label className="cursor-pointer max-w-full">
                            <div className={cn(
                                'flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed text-sm font-semibold transition-all',
                                'border-indigo-300 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400',
                                'hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                            )}>
                                <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{excelFile ? excelFile.name : 'Choose Excel File (.xlsx / .xls)'}</span>
                            </div>
                            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
                        </label>
                        {excelFile && parsedRows.length > 0 && (
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                                ✓ {parsedRows.length} rows parsed
                            </span>
                        )}
                    </div>

                    {parseError && (
                        <div className="flex items-center gap-2 text-rose-500 text-xs">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            {parseError}
                        </div>
                    )}

                    {parsedRows.length > 0 && (
                        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40">
                                        {['#', 'Labour ID', 'Name', 'Category', 'Days Present'].map(t => (
                                            <th key={t} className="px-3 py-2 text-left font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {parsedRows.map((r, i) => (
                                        <tr key={`${r.LabourId}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                            <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                                            <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-200">{r.LabourId}</td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.LabourName}</td>
                                            <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold">{r.Category}</span></td>
                                            <td className="px-3 py-2 font-medium">{r.DaysWorked}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <Btn onClick={handleUpload} loading={stagingLoading} disabled={!parsedRows.length || !form.ccCode}>
                            <Upload className="h-3.5 w-3.5" /> Upload & Continue to Preview
                        </Btn>
                    </div>
                    {stagingError && <p className="text-xs text-rose-500">{stagingError}</p>}
                </div>
            </SectionCard>
        </div>
    );

    // ── Step 1: Preview + amounts
    const renderPreview = () => {
        const validRows = previewRows.filter(r => r.ValidationStatus === 'Valid');
        const rowErrors = {};
        previewRows.forEach(r => { rowErrors[r.LabourId] = advanceRowError(r, amounts[r.LabourId]); });
        const hasErrors = Object.values(rowErrors).some(Boolean);
        const amt = (r) => Number(amounts[r.LabourId]) || 0;
        const given = previewRows.filter(r => amt(r) > 0);
        const total = given.reduce((s, r) => s + amt(r), 0);

        const groups = [];
        const own = previewRows.filter(isOwnLabour);
        if (own.length) groups.push({ key: '__own__', label: 'Own Labour', isOwn: true, rows: own });
        const ctr = {};
        previewRows.filter(r => !isOwnLabour(r)).forEach(r => {
            const name = r.ContractorName || r.ContractorCode || 'Unknown';
            (ctr[name] = ctr[name] || []).push(r);
        });
        Object.entries(ctr).forEach(([name, rows]) => groups.push({ key: `__ctr__${name}`, label: `Contractor — ${name}`, isOwn: false, rows }));

        const fillAvailable = () => {
            const next = {};
            validRows.forEach(r => { if (Number(r.AvailableAmount) > 0) next[r.LabourId] = String(r.AvailableAmount); });
            setAmounts(next);
        };

        const handleGenerate = () => {
            if (!uploadBatchId) { toast.warning('Upload the advance sheet first'); return; }
            if (!form.advanceDate) { toast.warning('Select the advance date'); return; }
            if (hasErrors) { toast.warning('Correct the highlighted amounts'); return; }
            if (!given.length) { toast.warning('Enter the advance amount for at least one labour'); return; }
            setLastGenerateError('');
            dispatch(generateLabourAdvance(buildGenerateAdvancePayload({
                form, uploadBatchId, previewRows, amounts,
                roleId: userData?.roleId, createdBy: userData?.userName || userData?.empCode,
            })));
        };

        const handleDownloadPreviewExcel = () => {
            const aoa = [['#', 'Labour ID', 'Name', 'Category', 'Labour Type', 'Contractor', 'Days Present',
                'Daily Rate', 'Earned', 'Unsettled Advance', 'Available', 'Advance', 'Status']];
            previewRows.forEach((r, i) => aoa.push([
                i + 1, r.LabourId, r.LabourName, r.Category, r.LabourType || 'Own Labour', r.ContractorName || '',
                r.DaysWorked, r.DailyRate, r.EarnedAmount, r.Unsettled, r.AvailableAmount, amt(r), r.ValidationStatus,
            ]));
            aoa.push([]);
            aoa.push(['', 'TOTAL', `${given.length} labour`, '', '', '', '', '', '', '', '', total, '']);
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws['!cols'] = [{ wch: 4 }, { wch: 12 }, { wch: 22 }, { wch: 8 }, { wch: 14 }, { wch: 22 }, { wch: 12 },
                { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 24 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Advance Preview');
            const monthName = MONTHS.find(m => m.v === form.month)?.l || form.month;
            XLSX.writeFile(wb, `LabourAdvance_Preview_${form.ccCode}_${monthName}_${form.year}.xlsx`);
        };

        const th = 'px-2 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap';
        const numInput = 'w-24 rounded-lg border-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 text-right disabled:opacity-50';
        let rowNum = 0;

        return (
            <div className="space-y-6">
                {previewRows.some(r => r.ValidationStatus !== 'Valid') && (
                    <div className="flex items-center gap-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        Rows shown in red cannot get an advance; the reason is in the Status column.
                    </div>
                )}

                <SectionCard
                    title={`Labour-wise Advance — ${validRows.length} Valid / ${previewRows.length} Total`}
                    icon={Users}
                    titleRight={
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={fillAvailable} disabled={!validRows.length}
                                className="px-3 py-1.5 rounded-lg border-2 border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300 text-xs font-semibold hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40">
                                Fill Available
                            </button>
                            <button onClick={() => setAmounts({})}
                                className="px-3 py-1.5 rounded-lg border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                Clear
                            </button>
                            <button onClick={handleDownloadPreviewExcel} disabled={!previewRows.length}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold transition-all shadow-sm">
                                <Download className="h-3.5 w-3.5" /> Download Excel
                            </button>
                        </div>
                    }
                >
                    {previewLoading ? (
                        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                    ) : previewError ? (
                        <p className="p-6 text-rose-500 text-sm">{previewError}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                        <th className={cn(th, 'text-left')}>#</th>
                                        <th className={cn(th, 'text-left')}>Labour ID</th>
                                        <th className={cn(th, 'text-left')}>Name</th>
                                        <th className={cn(th, 'text-center')}>Cat</th>
                                        <th className={cn(th, 'text-center')}>Days</th>
                                        <th className={cn(th, 'text-right text-blue-600 bg-blue-50/50 dark:bg-blue-900/10')}>Daily Rate</th>
                                        <th className={cn(th, 'text-right text-blue-600 bg-blue-50/50 dark:bg-blue-900/10')}>Earned</th>
                                        <th className={cn(th, 'text-right text-rose-600 bg-rose-50/50 dark:bg-rose-900/10')}>Unsettled</th>
                                        <th className={cn(th, 'text-right text-emerald-600 bg-emerald-50/50 dark:bg-emerald-900/10')}>Available</th>
                                        <th className={cn(th, 'text-right')}>Advance (₹)</th>
                                        <th className={cn(th, 'text-left')}>Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {groups.map(g => (
                                        <React.Fragment key={g.key}>
                                            <tr className="bg-indigo-50/80 dark:bg-indigo-900/30">
                                                <td colSpan={11} className="px-3 py-1.5 border-t-2 border-indigo-200 dark:border-indigo-700">
                                                    <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider">{g.label}</span>
                                                </td>
                                            </tr>
                                            {g.rows.map(r => {
                                                rowNum++;
                                                const ok = r.ValidationStatus === 'Valid';
                                                const err = rowErrors[r.LabourId];
                                                return (
                                                    <tr key={r.LabourId} className={cn(
                                                        'hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10',
                                                        !ok && 'bg-rose-50/60 dark:bg-rose-900/10 opacity-70'
                                                    )}>
                                                        <td className="px-2 py-2 text-gray-400">{rowNum}</td>
                                                        <td className="px-2 py-2 font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">{r.LabourId}</td>
                                                        <td className="px-2 py-2 text-gray-700 dark:text-gray-200 whitespace-nowrap">{r.LabourName}</td>
                                                        <td className="px-2 py-2 text-center">
                                                            <span className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold">{r.Category}</span>
                                                        </td>
                                                        <td className="px-2 py-2 text-center font-medium text-gray-700 dark:text-gray-200">{r.DaysWorked}</td>
                                                        <td className="px-2 py-2 text-right text-gray-600 dark:text-gray-300 bg-blue-50/30 dark:bg-blue-900/5">₹{fmt(r.DailyRate)}</td>
                                                        <td className="px-2 py-2 text-right font-semibold text-blue-700 dark:text-blue-300 bg-blue-50/30 dark:bg-blue-900/5">₹{fmt(r.EarnedAmount)}</td>
                                                        <td className="px-2 py-2 text-right text-rose-600 dark:text-rose-400 bg-rose-50/30 dark:bg-rose-900/5">{Number(r.Unsettled) > 0 ? `₹${fmt(r.Unsettled)}` : '—'}</td>
                                                        <td className="px-2 py-2 text-right font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/5">₹{fmt(r.AvailableAmount)}</td>
                                                        <td className="px-2 py-2 text-right">
                                                            <input type="number" min="0" step="1"
                                                                className={cn(numInput, err ? 'border-rose-400 dark:border-rose-600' : 'border-gray-200 dark:border-gray-600')}
                                                                placeholder="0" value={amounts[r.LabourId] ?? ''}
                                                                onChange={e => setAmounts(p => ({ ...p, [r.LabourId]: e.target.value }))}
                                                                disabled={!ok || !(Number(r.AvailableAmount) > 0)}
                                                                title={err || ''} />
                                                            {err && <div className="text-[10px] text-rose-500 mt-0.5">{err}</div>}
                                                        </td>
                                                        <td className={cn('px-2 py-2 whitespace-nowrap', ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400 font-semibold')}>
                                                            {r.ValidationStatus}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-500 font-bold">
                                        <td colSpan={9} className="px-2 py-2.5 text-xs font-bold text-gray-700 dark:text-gray-200 uppercase">
                                            Total Advance — {given.length} labour
                                        </td>
                                        <td className="px-2 py-2.5 text-right text-xs font-bold text-emerald-600 dark:text-emerald-400">₹{fmt(total)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </SectionCard>

                {groups.length > 0 && (
                    <SectionCard title="Labour Type Summary" icon={Users}>
                        <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {groups.map(g => {
                                const withAmt = g.rows.filter(r => amt(r) > 0);
                                return (
                                    <div key={g.key} className={cn('rounded-xl border p-4',
                                        g.isOwn ? 'border-indigo-200 dark:border-indigo-700 bg-indigo-50/30 dark:bg-indigo-900/10'
                                                : 'border-teal-200 dark:border-teal-700 bg-teal-50/30 dark:bg-teal-900/10')}>
                                        <div className="flex items-center justify-between gap-2 mb-3">
                                            <span className={cn('px-2 py-0.5 rounded-lg text-xs font-bold',
                                                g.isOwn ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                                                        : 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300')}>
                                                {g.label}
                                            </span>
                                            <span className="text-xs text-gray-500">{withAmt.length} / {g.rows.length} labour</span>
                                        </div>
                                        {[
                                            ['Earned', g.rows.reduce((s, r) => s + (Number(r.EarnedAmount) || 0), 0)],
                                            ['Available', g.rows.reduce((s, r) => s + (Number(r.AvailableAmount) || 0), 0)],
                                        ].map(([label, val]) => (
                                            <div key={label} className="flex justify-between text-xs py-0.5">
                                                <span className="text-gray-500 dark:text-gray-400">{label}</span>
                                                <span className="text-gray-700 dark:text-gray-200">₹{fmt(val)}</span>
                                            </div>
                                        ))}
                                        <div className="flex justify-between text-xs font-bold border-t border-gray-100 dark:border-gray-700 pt-2 mt-2">
                                            <span className="text-gray-700 dark:text-gray-200">Advance</span>
                                            <span className="text-emerald-600 dark:text-emerald-400">₹{fmt(withAmt.reduce((s, r) => s + amt(r), 0))}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </SectionCard>
                )}

                {lastGenerateError && (
                    <div className="rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-rose-700 dark:text-rose-400 mb-1">Generation Failed</p>
                                <p className="text-xs text-rose-600 dark:text-rose-300 leading-relaxed">{lastGenerateError}</p>
                            </div>
                            <button onClick={() => setLastGenerateError('')}
                                className="text-rose-400 hover:text-rose-600 text-xs font-bold flex-shrink-0">✕</button>
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <Btn variant="secondary" onClick={() => setStep(0)}>
                        <RotateCcw className="h-3.5 w-3.5" /> Back
                    </Btn>
                    <Btn variant="success" onClick={handleGenerate} loading={generateLoading}
                        disabled={!given.length || hasErrors}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Generate & Submit for Approval (₹{fmt(total)})
                    </Btn>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ───────────────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />

                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <HandCoins className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">HR Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">{menuData?.name || 'Labour Advance Generation'}</h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Upload days present, enter advance amounts, and submit for approval</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {step === 1 && (
                                <button onClick={handleReset}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-white text-xs font-semibold transition-all">
                                    <RotateCcw className="h-3.5 w-3.5" /> Reset
                                </button>
                            )}
                            <div className="hidden sm:flex items-center gap-2 text-indigo-200">
                                <div className="text-right">
                                    <p className="text-xs uppercase tracking-wider">Module</p>
                                    <p className="text-sm font-bold text-white">HR / Labour Advance</p>
                                </div>
                                <Navigation className="h-5 w-5 opacity-60" />
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <Steps current={step} />
                    </div>
                </div>
            </div>

            {/* ── Page Content ─────────────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto space-y-6">
                {step === 0 && renderSetup()}
                {step === 1 && renderPreview()}
                {step === 2 && (
                    detailLoading
                        ? <div className="py-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
                        : detailError
                            ? <p className="text-sm text-rose-500">{detailError}</p>
                            : <SummaryView detail={detail} onReset={handleReset} />
                )}
            </div>
        </div>
    );
};

export default LabourAdvanceGeneration;
